import { useState, useEffect, useRef, useCallback } from 'react'
import { estimatesService, type MapPin } from '@/services/estimates-service'
import { assetsService } from '@/services/assets-service'
import {
  type MapShape,
  type MapMarker,
  type MapViewport,
  SUBTRACT_COLOR,
  MARKER_COLOR,
  SHAPE_COLORS,
  parseMapData,
  netPolygonSF,
  grossPolygonSF,
  subtractedSF,
  totalLineLF,
  nextLabel,
  newId,
  escapeHtml,
} from '@/lib/map-data'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || ''

interface MapPanelProps {
  estimateId: string
  jobAddress?: string
  jobCity?: string
  jobState?: string
  jobZip?: string
  initialMapData?: any
  onMapChange?: (mapJson: any, totalSquareFootage: number) => void
}

type AreaUnit = 'sf' | 'sy' | 'acres'
type LineUnit = 'lf' | 'ft'

const AREA_UNIT_LABELS: Record<AreaUnit, string> = { sf: 'SF', sy: 'SY', acres: 'Acres' }
const LINE_UNIT_LABELS: Record<LineUnit, string> = { lf: 'LF', ft: 'Feet' }

function convertArea(sf: number, unit: AreaUnit): number {
  switch (unit) {
    case 'sy': return sf / 9
    case 'acres': return sf / 43560
    default: return sf
  }
}

function convertLine(lf: number, unit: LineUnit): number {
  // LF and Feet are the same thing — included for label clarity
  return lf
  void unit
}

function formatValue(value: number, unit: string): string {
  if (unit === 'acres') return `${value.toFixed(3)} ${AREA_UNIT_LABELS.acres}`
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k ${unit}`
  return `${Math.round(value)} ${unit}`
}

// Calculate polygon area in square feet using the Shoelace formula
function calcPolygonAreaSF(coords: [number, number][]): number {
  if (coords.length < 3) return 0
  const centerLat = coords.reduce((s, c) => s + c[1], 0) / coords.length
  const lngToFt = Math.cos((centerLat * Math.PI) / 180) * 364567.2
  const latToFt = 364567.2
  const pts = coords.map(([lng, lat]) => [lng * lngToFt, lat * latToFt] as [number, number])
  let area = 0
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length
    area += pts[i][0] * pts[j][1]
    area -= pts[j][0] * pts[i][1]
  }
  return Math.abs(area / 2)
}

// Calculate line length in linear feet
function calcLineLengthLF(coords: [number, number][]): number {
  if (coords.length < 2) return 0
  let total = 0
  for (let i = 1; i < coords.length; i++) {
    const [lng1, lat1] = coords[i - 1]
    const [lng2, lat2] = coords[i]
    const dLat = (lat2 - lat1) * 364567.2
    const dLng = (lng2 - lng1) * Math.cos(((lat1 + lat2) / 2 * Math.PI) / 180) * 364567.2
    total += Math.sqrt(dLat * dLat + dLng * dLng)
  }
  return total
}

// Simplify freehand coordinates by removing points that are too close together
function simplifyCoords(coords: [number, number][], tolerance: number): [number, number][] {
  if (coords.length < 3) return coords
  const result: [number, number][] = [coords[0]]
  for (let i = 1; i < coords.length - 1; i++) {
    const [x1, y1] = result[result.length - 1]
    const [x2, y2] = coords[i]
    const dist = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2)
    if (dist >= tolerance) result.push(coords[i])
  }
  result.push(coords[coords.length - 1])
  return result
}

export function MapPanel({ estimateId, jobAddress, jobCity, jobState, jobZip, initialMapData, onMapChange }: MapPanelProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [mapError, setMapError] = useState<string | null>(null)
  // Parse initialMapData — see parseMapData for the formats still supported
  const parsedInitial = parseMapData(initialMapData)

  const [shapes, setShapes] = useState<MapShape[]>(parsedInitial.shapes)
  const [markers, setMarkers] = useState<MapMarker[]>(parsedInitial.markers)
  const savedViewport = useRef(parsedInitial.viewport)
  const [activeTool, setActiveTool] = useState<'select' | 'polygon' | 'line' | 'freehand' | 'subtract' | 'marker'>('select')
  const [drawingCoords, setDrawingCoords] = useState<[number, number][]>([])
  const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null)
  const [editingLabel, setEditingLabel] = useState<string | null>(null)
  const [colorPickerFor, setColorPickerFor] = useState<string | null>(null)
  const colorPopoverRef = useRef<HTMLDivElement>(null)
  const [addressQuery, setAddressQuery] = useState(() => {
    return [jobAddress, jobCity, jobState, jobZip].filter(Boolean).join(', ')
  })
  const [geocoding, setGeocoding] = useState(false)
  const [areaUnit, setAreaUnit] = useState<AreaUnit>('sf')
  const [lineUnit, setLineUnit] = useState<LineUnit>('lf')
  const [isDrawingFreehand, setIsDrawingFreehand] = useState(false)

  const shapesRef = useRef(shapes)
  const markersRef = useRef(markers)
  const activeToolRef = useRef(activeTool)
  const drawingCoordsRef = useRef(drawingCoords)
  const isDrawingFreehandRef = useRef(isDrawingFreehand)
  const onMapChangeRef = useRef(onMapChange)
  onMapChangeRef.current = onMapChange

  // Keep refs in sync
  useEffect(() => { shapesRef.current = shapes }, [shapes])
  useEffect(() => { markersRef.current = markers }, [markers])
  useEffect(() => { activeToolRef.current = activeTool }, [activeTool])
  useEffect(() => { drawingCoordsRef.current = drawingCoords }, [drawingCoords])
  useEffect(() => { isDrawingFreehandRef.current = isDrawingFreehand }, [isDrawingFreehand])

  const saveTimeout = useRef<ReturnType<typeof setTimeout>>()
  const initialLoadDone = useRef(false)

  // Single writer for mapJson. Always reads the latest shapes/markers from refs
  // so every caller — debounced edit, viewport moveend, unmount, page hide —
  // persists the same complete payload and can never drop the other half.
  const persist = useCallback((reason: string) => {
    const nextShapes = shapesRef.current
    const nextMarkers = markersRef.current
    const totalSF = netPolygonSF(nextShapes)
    // Include viewport state so we can restore exact position on reload
    const map = mapRef.current
    let viewport: MapViewport | undefined
    if (map) {
      const c = map.getCenter()
      viewport = { center: [c.lng, c.lat], zoom: map.getZoom() }
    }
    const mapJson = { shapes: nextShapes, markers: nextMarkers, viewport }
    onMapChangeRef.current?.(mapJson, totalSF)
    return estimatesService.update(estimateId, {
      mapJson: mapJson as any,
      totalSquareFootage: totalSF,
    } as any).then(() => {
      console.log(`[MapPanel] Map saved (${reason})`, nextShapes.length, 'shapes', nextMarkers.length, 'markers')
    }).catch((err) => {
      console.error(`[MapPanel] Failed to save map data (${reason}):`, err?.response?.data || err?.message || err)
    })
  }, [estimateId])

  // The map init effect runs once and closes over the first render's persist,
  // so reach it through a ref instead.
  const persistRef = useRef(persist)
  useEffect(() => { persistRef.current = persist }, [persist])

  // Run any save still sitting in the debounce window, right now.
  const flushPendingSave = useCallback((reason: string) => {
    if (!saveTimeout.current) return
    clearTimeout(saveTimeout.current)
    saveTimeout.current = undefined
    persist(reason)
  }, [persist])

  // Auto-save when shapes or markers change (includes map viewport for restore)
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true
      return
    }
    clearTimeout(saveTimeout.current)
    saveTimeout.current = setTimeout(() => {
      saveTimeout.current = undefined
      persist('edit')
    }, 1500)
    return () => clearTimeout(saveTimeout.current)
  }, [shapes, markers, persist])

  // Flush pending save on unmount (tab switch)
  useEffect(() => {
    return () => { flushPendingSave('unmount') }
  }, [flushPendingSave])

  // Flush pending save when the page is hidden (tab close, app switch, mobile
  // background). Without this, edits made inside the debounce window are lost
  // on a refresh — the unmount cleanup does not run on a real page teardown.
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') flushPendingSave('pagehide') }
    document.addEventListener('visibilitychange', onHide)
    return () => document.removeEventListener('visibilitychange', onHide)
  }, [flushPendingSave])

  // Geocode job address
  const geocodeAddress = useCallback(async (): Promise<[number, number] | null> => {
    const parts = [jobAddress, jobCity, jobState, jobZip].filter(Boolean)
    if (parts.length === 0) return null
    const query = encodeURIComponent(parts.join(', '))
    try {
      const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?access_token=${MAPBOX_TOKEN}&limit=1`)
      const data = await res.json()
      if (data.features && data.features.length > 0) {
        return data.features[0].center as [number, number]
      }
    } catch { /* fall through */ }
    return null
  }, [jobAddress, jobCity, jobState, jobZip])

  // Initialize map
  useEffect(() => {
    if (!MAPBOX_TOKEN) {
      setMapError('Mapbox token not configured. Add VITE_MAPBOX_TOKEN to your environment.')
      return
    }
    if (!mapContainerRef.current) return

    let cancelled = false

    const initMap = async () => {
      if (cancelled || !mapContainerRef.current) return

      // Use saved viewport if available, otherwise geocode the job address
      const sv = savedViewport.current
      const center = sv?.center || (await geocodeAddress()) || [-117.16, 32.72] as [number, number]
      const zoom = sv?.zoom || 18

      mapboxgl.accessToken = MAPBOX_TOKEN
      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/satellite-streets-v12',
        center,
        zoom,
        pitch: 0,
        preserveDrawingBuffer: true, // Required for canvas.toDataURL() screenshot
      })

      map.addControl(new mapboxgl.NavigationControl(), 'top-right')

      map.on('load', () => {
        if (cancelled) return
        mapRef.current = map
        setMapLoaded(true)

        // Add sources
        map.addSource('shapes-polygons', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
        map.addSource('shapes-subtract', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
        map.addSource('shapes-lines', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
        map.addSource('drawing', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })

        // Polygon fill
        map.addLayer({ id: 'shapes-polygons-fill', type: 'fill', source: 'shapes-polygons', paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.25 } })
        // Polygon outline
        map.addLayer({ id: 'shapes-polygons-outline', type: 'line', source: 'shapes-polygons', paint: { 'line-color': ['get', 'color'], 'line-width': 2.5 } })
        // Subtract (cut-out) fill — rendered above area fills so overlaps read clearly
        map.addLayer({ id: 'shapes-subtract-fill', type: 'fill', source: 'shapes-subtract', paint: { 'fill-color': SUBTRACT_COLOR, 'fill-opacity': 0.3 } })
        // Subtract outline — dashed to signal a removed area
        map.addLayer({ id: 'shapes-subtract-outline', type: 'line', source: 'shapes-subtract', paint: { 'line-color': SUBTRACT_COLOR, 'line-width': 2.5, 'line-dasharray': [2, 2] } })
        // Lines
        map.addLayer({ id: 'shapes-lines-layer', type: 'line', source: 'shapes-lines', paint: { 'line-color': ['get', 'color'], 'line-width': 3, 'line-dasharray': [2, 1] } })
        // No vertex dots on FINISHED shapes. They were decoration — nothing clicks
        // or drags them, and shapes are selected from the measurements list — and
        // on a lot with several areas drawn they read as clutter. The dots that
        // matter are the ones below, while a shape is still being drawn.
        // Drawing preview
        map.addLayer({ id: 'drawing-line', type: 'line', source: 'drawing', paint: { 'line-color': '#3B82F6', 'line-width': 2, 'line-dasharray': [3, 2] } })
        map.addLayer({ id: 'drawing-points', type: 'circle', source: 'drawing', paint: { 'circle-radius': 5, 'circle-color': '#3B82F6', 'circle-stroke-width': 2, 'circle-stroke-color': '#fff' } })

        renderShapes(map, shapesRef.current)

        // If we have saved shapes but no saved viewport (legacy data), fit bounds to show all shapes
        if (shapesRef.current.length > 0 && !savedViewport.current) {
          const allCoords = shapesRef.current.flatMap(s => s.coords)
          if (allCoords.length > 0) {
            const bounds = allCoords.reduce(
              (b, [lng, lat]) => {
                b[0][0] = Math.min(b[0][0], lng)
                b[0][1] = Math.min(b[0][1], lat)
                b[1][0] = Math.max(b[1][0], lng)
                b[1][1] = Math.max(b[1][1], lat)
                return b
              },
              [[Infinity, Infinity], [-Infinity, -Infinity]] as [[number, number], [number, number]],
            )
            map.fitBounds(bounds, { padding: 80, maxZoom: 19, duration: 0 })
          }
        }
        // Saved viewport is already applied via the initial center/zoom above
      })

      // Click handler for polygon/line drawing and marker placement
      map.on('click', (e: any) => {
        const tool = activeToolRef.current
        if (tool === 'select' || tool === 'freehand') return

        const coord: [number, number] = [e.lngLat.lng, e.lngLat.lat]

        // Marker tool: drop a pin at the clicked location (stays active for more)
        if (tool === 'marker') {
          const newMarker: MapMarker = {
            id: newId('marker'),
            coord,
            label: nextLabel(markersRef.current.map(m => m.label), 'Marker'),
            note: '',
            color: MARKER_COLOR,
          }
          setMarkers(prev => [...prev, newMarker])
          setSelectedShapeId(newMarker.id)
          return
        }

        const newCoords = [...drawingCoordsRef.current, coord]
        setDrawingCoords(newCoords)
        updateDrawingPreview(map, newCoords, tool)
      })

      // Double-click to finish polygon/line
      map.on('dblclick', (e: any) => {
        const tool = activeToolRef.current
        if (tool === 'select' || tool === 'freehand') return

        e.preventDefault()
        const coords = drawingCoordsRef.current
        if (((tool === 'polygon' || tool === 'subtract') && coords.length >= 3) || (tool === 'line' && coords.length >= 2)) {
          finishDrawing(coords, tool)
        }
      })

      // Freehand drawing: mousedown → track mousemove → mouseup to finish
      map.on('mousedown', (e: any) => {
        if (activeToolRef.current !== 'freehand') return
        e.preventDefault()
        map.dragPan.disable()
        setIsDrawingFreehand(true)
        isDrawingFreehandRef.current = true
        const coord: [number, number] = [e.lngLat.lng, e.lngLat.lat]
        setDrawingCoords([coord])
        drawingCoordsRef.current = [coord]
      })

      map.on('mousemove', (e: any) => {
        if (!isDrawingFreehandRef.current) return
        const coord: [number, number] = [e.lngLat.lng, e.lngLat.lat]
        const newCoords = [...drawingCoordsRef.current, coord]
        setDrawingCoords(newCoords)
        drawingCoordsRef.current = newCoords
        updateDrawingPreview(map, newCoords, 'polygon')
      })

      map.on('mouseup', () => {
        if (!isDrawingFreehandRef.current) return
        map.dragPan.enable()
        setIsDrawingFreehand(false)
        isDrawingFreehandRef.current = false
        const coords = drawingCoordsRef.current
        if (coords.length >= 3) {
          // Simplify the freehand path to reduce noise
          const simplified = simplifyCoords(coords, 0.000005)
          finishDrawing(simplified.length >= 3 ? simplified : coords, 'polygon')
        } else {
          setDrawingCoords([])
          if (map.getSource('drawing')) {
            (map.getSource('drawing') as mapboxgl.GeoJSONSource).setData({ type: 'FeatureCollection', features: [] })
          }
        }
      })

      // Save viewport when user pans or zooms (debounced)
      let viewportSaveTimer: ReturnType<typeof setTimeout>
      map.on('moveend', () => {
        clearTimeout(viewportSaveTimer)
        viewportSaveTimer = setTimeout(() => {
          if (!mapRef.current) return
          persistRef.current('viewport')
        }, 2000)
      })

      map.getCanvas().style.cursor = ''
    }

    initMap()

    return () => {
      cancelled = true
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Update cursor when tool changes
  useEffect(() => {
    if (!mapRef.current) return
    mapRef.current.getCanvas().style.cursor = activeTool === 'select' ? '' : 'crosshair'
    // Tint the drawing preview red while cutting out, blue otherwise
    const previewColor = activeTool === 'subtract' ? SUBTRACT_COLOR : '#3B82F6'
    if (mapRef.current.getLayer('drawing-line')) mapRef.current.setPaintProperty('drawing-line', 'line-color', previewColor)
    if (mapRef.current.getLayer('drawing-points')) mapRef.current.setPaintProperty('drawing-points', 'circle-color', previewColor)
    if (activeTool === 'select') {
      setDrawingCoords([])
      if (mapRef.current.getSource('drawing')) {
        ;(mapRef.current.getSource('drawing') as mapboxgl.GeoJSONSource).setData({ type: 'FeatureCollection', features: [] })
      }
    }
  }, [activeTool])

  // Re-render shapes when they change or units change
  useEffect(() => {
    if (mapRef.current && mapLoaded) {
      renderShapes(mapRef.current, shapes)
    }
  }, [shapes, mapLoaded, areaUnit, lineUnit]) // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Field pins from mobile app (lettered: A, B, C...) ----
  const [fieldPins, setFieldPins] = useState<MapPin[]>([])
  const pinMarkersRef = useRef<mapboxgl.Marker[]>([])
  const pinPopupRef = useRef<mapboxgl.Popup | null>(null)

  // Convert index to letter: 0→A, 1→B, ... 25→Z, 26→AA, etc.
  function indexToLetter(i: number): string {
    let result = ''
    let n = i
    do {
      result = String.fromCharCode(65 + (n % 26)) + result
      n = Math.floor(n / 26) - 1
    } while (n >= 0)
    return result
  }

  // Load field pins
  useEffect(() => {
    estimatesService.getMapPins(estimateId)
      .then(setFieldPins)
      .catch(() => {}) // silent if endpoint doesn't exist yet
  }, [estimateId])

  // Render lettered pin markers
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded) return

    // Clear old markers
    pinMarkersRef.current.forEach(m => m.remove())
    pinMarkersRef.current = []

    fieldPins.forEach((pin, idx) => {
      const letter = indexToLetter(idx)
      const el = document.createElement('div')
      el.className = 'map-field-pin'

      // Lettered circle marker
      el.innerHTML = `<div class="map-field-pin-letter">${letter}</div>`

      el.addEventListener('click', (e) => {
        e.stopPropagation()
        // Close any existing popup
        if (pinPopupRef.current) pinPopupRef.current.remove()

        // Build popup HTML — clean card with photo + note only.
        // Both values are field-user input from the mobile app and are stored
        // raw, so they must be escaped before going anywhere near innerHTML.
        const photoHtml = pin.photoUrl
          ? `<img src="${escapeHtml(pin.photoUrl)}" alt="" class="mfp-photo" />`
          : ''
        const noteHtml = pin.noteText
          ? `<p class="mfp-note">${escapeHtml(pin.noteText)}</p>`
          : ''
        const html = `<div class="mfp-card">${photoHtml}${noteHtml}</div>`

        const popup = new mapboxgl.Popup({ maxWidth: '280px', closeButton: true, closeOnClick: true, className: 'mfp-popup' })
          .setLngLat([pin.longitude, pin.latitude])
          .setHTML(html)
          .addTo(map)

        pinPopupRef.current = popup
        popup.on('close', () => { pinPopupRef.current = null })
      })

      const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([pin.longitude, pin.latitude])
        .addTo(map)

      pinMarkersRef.current.push(marker)
    })

    return () => {
      pinMarkersRef.current.forEach(m => m.remove())
      pinMarkersRef.current = []
      if (pinPopupRef.current) { pinPopupRef.current.remove(); pinPopupRef.current = null }
    }
  }, [fieldPins, mapLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  // ---- User-placed location markers (pins with a label + note) ----
  const userMarkerObjsRef = useRef<mapboxgl.Marker[]>([])
  const userPinPopupRef = useRef<mapboxgl.Popup | null>(null)

  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded) return

    userMarkerObjsRef.current.forEach(m => m.remove())
    userMarkerObjsRef.current = []

    markers.forEach(marker => {
      const el = document.createElement('div')
      el.className = 'map-user-pin'
      el.innerHTML = `
        <span class="map-user-pin-label">${escapeHtml(marker.label)}</span>
        <svg width="22" height="28" viewBox="0 0 24 30" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 0C5.37 0 0 5.37 0 12c0 8.25 12 18 12 18s12-9.75 12-18C24 5.37 18.63 0 12 0z" fill="${marker.color}" stroke="#fff" stroke-width="1.5"/>
          <circle cx="12" cy="12" r="4.5" fill="#fff"/>
        </svg>`

      el.addEventListener('click', (e) => {
        e.stopPropagation()
        if (userPinPopupRef.current) userPinPopupRef.current.remove()
        const noteHtml = marker.note
          ? `<p class="mup-note">${escapeHtml(marker.note)}</p>`
          : `<p class="mup-note mup-note-empty">No note added</p>`
        const html = `<div class="mup-card"><strong class="mup-label">${escapeHtml(marker.label)}</strong>${noteHtml}</div>`
        const popup = new mapboxgl.Popup({ maxWidth: '240px', closeButton: true, closeOnClick: true, className: 'mfp-popup' })
          .setLngLat(marker.coord)
          .setHTML(html)
          .addTo(map)
        userPinPopupRef.current = popup
        popup.on('close', () => { userPinPopupRef.current = null })
      })

      const mk = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat(marker.coord)
        .addTo(map)
      userMarkerObjsRef.current.push(mk)
    })

    return () => {
      userMarkerObjsRef.current.forEach(m => m.remove())
      userMarkerObjsRef.current = []
      if (userPinPopupRef.current) { userPinPopupRef.current.remove(); userPinPopupRef.current = null }
    }
  }, [markers, mapLoaded])

  // Build a lookup: assetId → letter (for use by AssetsPanel)
  const pinLetterMap = useRef<Map<string, string>>(new Map())
  useEffect(() => {
    const m = new Map<string, string>()
    fieldPins.forEach((pin, idx) => {
      if (pin.photoAssetId) m.set(pin.photoAssetId, indexToLetter(idx))
    })
    pinLetterMap.current = m
    // Dispatch custom event so AssetsPanel can pick it up
    window.dispatchEvent(new CustomEvent('paver-pin-letters', { detail: Object.fromEntries(m) }))
  }, [fieldPins])

  function getDisplayMeasurement(s: MapShape): string {
    if (s.type === 'polygon' || s.type === 'subtract') {
      const val = convertArea(s.measurement, areaUnit)
      const formatted = formatValue(val, AREA_UNIT_LABELS[areaUnit])
      return s.type === 'subtract' ? `−${formatted}` : formatted
    }
    const val = convertLine(s.measurement, lineUnit)
    return formatValue(val, LINE_UNIT_LABELS[lineUnit])
  }

  function renderShapes(map: any, shapes: MapShape[]) {
    const polygons = shapes.filter(s => s.type === 'polygon')
    const subtracts = shapes.filter(s => s.type === 'subtract')
    const lines = shapes.filter(s => s.type === 'line')

    const polygonFeatures = polygons.map(s => ({
      type: 'Feature' as const,
      properties: { id: s.id, color: s.color, label: s.label },
      geometry: { type: 'Polygon' as const, coordinates: [[...s.coords, s.coords[0]]] },
    }))

    const subtractFeatures = subtracts.map(s => ({
      type: 'Feature' as const,
      properties: { id: s.id, color: SUBTRACT_COLOR, label: s.label },
      geometry: { type: 'Polygon' as const, coordinates: [[...s.coords, s.coords[0]]] },
    }))

    const lineFeatures = lines.map(s => ({
      type: 'Feature' as const,
      properties: { id: s.id, color: s.color, label: s.label },
      geometry: { type: 'LineString' as const, coordinates: s.coords },
    }))

    // A finished shape draws its outline and fill, and nothing else: the vertex
    // dots that used to sit on every corner are gone. See the layer setup above.
    if (map.getSource('shapes-polygons')) (map.getSource('shapes-polygons') as mapboxgl.GeoJSONSource).setData({ type: 'FeatureCollection', features: polygonFeatures })
    if (map.getSource('shapes-subtract')) (map.getSource('shapes-subtract') as mapboxgl.GeoJSONSource).setData({ type: 'FeatureCollection', features: subtractFeatures })
    if (map.getSource('shapes-lines')) (map.getSource('shapes-lines') as mapboxgl.GeoJSONSource).setData({ type: 'FeatureCollection', features: lineFeatures })

    // Remove old labels
    shapes.forEach(s => {
      if (map.getLayer(`label-${s.id}`)) map.removeLayer(`label-${s.id}`)
      if (map.getSource(`label-${s.id}`)) map.removeSource(`label-${s.id}`)
    })

    // Add measurement labels at centroids
    shapes.forEach(s => {
      const center = getCentroid(s.coords)
      const text = `${s.label}\n${getDisplayMeasurement(s)}`
      const sourceId = `label-${s.id}`

      if (map.getSource(sourceId)) {
        ;(map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData({
          type: 'FeatureCollection',
          features: [{ type: 'Feature', properties: { text }, geometry: { type: 'Point', coordinates: center } }],
        })
      } else {
        map.addSource(sourceId, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [{ type: 'Feature', properties: { text }, geometry: { type: 'Point', coordinates: center } }],
          },
        })
        map.addLayer({
          id: `label-${s.id}`,
          type: 'symbol',
          source: sourceId,
          layout: {
            'text-field': ['get', 'text'],
            'text-size': 13,
            'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
            'text-anchor': 'center',
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': '#fff',
            'text-halo-color': 'rgba(0,0,0,0.7)',
            'text-halo-width': 1.5,
          },
        })
      }
    })
  }

  function getCentroid(coords: [number, number][]): [number, number] {
    const lng = coords.reduce((s, c) => s + c[0], 0) / coords.length
    const lat = coords.reduce((s, c) => s + c[1], 0) / coords.length
    return [lng, lat]
  }

  function updateDrawingPreview(map: any, coords: [number, number][], tool: string) {
    const isArea = tool === 'polygon' || tool === 'freehand' || tool === 'subtract'
    const features: any[] = []
    if (coords.length >= 2) {
      features.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: isArea ? 'Polygon' : 'LineString',
          coordinates: isArea ? [[...coords, coords[0]]] : coords,
        },
      })
    }
    coords.forEach(coord => {
      features.push({
        type: 'Feature',
        properties: {},
        geometry: { type: 'Point', coordinates: coord },
      })
    })
    if (map.getSource('drawing')) {
      ;(map.getSource('drawing') as mapboxgl.GeoJSONSource).setData({ type: 'FeatureCollection', features })
    }
  }

  function finishDrawing(coords: [number, number][], tool: 'polygon' | 'line' | 'subtract') {
    const colorIdx = shapesRef.current.length % SHAPE_COLORS.length
    const measurement = tool === 'line' ? calcLineLengthLF(coords) : calcPolygonAreaSF(coords)
    const prefix = tool === 'polygon' ? 'Area' : tool === 'subtract' ? 'Cut-out' : 'Line'
    const label = nextLabel(
      shapesRef.current.filter(s => s.type === tool).map(s => s.label),
      prefix,
    )
    const newShape: MapShape = {
      id: newId('shape'),
      type: tool,
      coords,
      label,
      measurement,
      color: tool === 'subtract' ? SUBTRACT_COLOR : SHAPE_COLORS[colorIdx],
    }
    setShapes(prev => [...prev, newShape])
    setDrawingCoords([])
    setActiveTool('select')
    if (mapRef.current?.getSource('drawing')) {
      ;(mapRef.current.getSource('drawing') as mapboxgl.GeoJSONSource).setData({ type: 'FeatureCollection', features: [] })
    }
  }

  const deleteShape = useCallback((id: string) => {
    const shape = shapesRef.current.find(s => s.id === id)
    const label = shape?.label ? `"${shape.label}"` : 'this measurement'
    if (!window.confirm(`Delete ${label}? The traced shape cannot be recovered.`)) return
    const map = mapRef.current
    if (map) {
      if (map.getLayer(`label-${id}`)) map.removeLayer(`label-${id}`)
      if (map.getSource(`label-${id}`)) map.removeSource(`label-${id}`)
    }
    setShapes(prev => prev.filter(s => s.id !== id))
    if (selectedShapeId === id) setSelectedShapeId(null)
  }, [selectedShapeId])

  const updateShapeLabel = useCallback((id: string, label: string) => {
    setShapes(prev => prev.map(s => s.id === id ? { ...s, label } : s))
    setEditingLabel(null)
  }, [])

  const updateShapeColor = useCallback((id: string, color: string) => {
    setShapes(prev => prev.map(s => s.id === id ? { ...s, color } : s))
    setColorPickerFor(null)
  }, [])

  // ---- Marker (location pin) actions ----
  const updateMarkerLabel = useCallback((id: string, label: string) => {
    setMarkers(prev => prev.map(m => m.id === id ? { ...m, label } : m))
    setEditingLabel(null)
  }, [])

  const updateMarkerNote = useCallback((id: string, note: string) => {
    setMarkers(prev => prev.map(m => m.id === id ? { ...m, note } : m))
  }, [])

  const updateMarkerColor = useCallback((id: string, color: string) => {
    setMarkers(prev => prev.map(m => m.id === id ? { ...m, color } : m))
    setColorPickerFor(null)
  }, [])

  const deleteMarker = useCallback((id: string) => {
    const marker = markersRef.current.find(m => m.id === id)
    const label = marker?.label ? `"${marker.label}"` : 'this marker'
    const withNote = marker?.note ? ' Its note will be deleted too.' : ''
    if (!window.confirm(`Delete ${label}?${withNote} This cannot be undone.`)) return
    setMarkers(prev => prev.filter(m => m.id !== id))
    setSelectedShapeId(prev => prev === id ? null : prev)
  }, [])

  // Close the color picker when clicking outside of it
  useEffect(() => {
    if (!colorPickerFor) return
    function onDown(e: MouseEvent) {
      if (colorPopoverRef.current && !colorPopoverRef.current.contains(e.target as Node)) {
        setColorPickerFor(null)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [colorPickerFor])

  // Address autocomplete suggestions
  const [suggestions, setSuggestions] = useState<{ place_name: string; center: [number, number] }[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const autocompleteTimeout = useRef<ReturnType<typeof setTimeout>>()

  const fetchSuggestions = useCallback((query: string) => {
    clearTimeout(autocompleteTimeout.current)
    if (query.trim().length < 3) { setSuggestions([]); return }
    autocompleteTimeout.current = setTimeout(async () => {
      try {
        const encoded = encodeURIComponent(query.trim())
        const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=5&types=address,place,poi`)
        const data = await res.json()
        if (data.features) {
          setSuggestions(data.features.map((f: any) => ({ place_name: f.place_name, center: f.center })))
          setShowSuggestions(true)
        }
      } catch { /* silent */ }
    }, 300)
  }, [])

  const selectSuggestion = useCallback((suggestion: { place_name: string; center: [number, number] }) => {
    setAddressQuery(suggestion.place_name)
    setSuggestions([])
    setShowSuggestions(false)
    if (mapRef.current) {
      mapRef.current.flyTo({ center: suggestion.center, zoom: 18, duration: 1500 })
    }
  }, [])

  const flyToAddress = useCallback(async () => {
    if (!addressQuery.trim() || !mapRef.current) return
    setGeocoding(true)
    setSuggestions([])
    setShowSuggestions(false)
    try {
      const query = encodeURIComponent(addressQuery.trim())
      const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?access_token=${MAPBOX_TOKEN}&limit=1`)
      const data = await res.json()
      if (data.features && data.features.length > 0) {
        const center = data.features[0].center as [number, number]
        mapRef.current.flyTo({ center, zoom: 18, duration: 1500 })
      }
    } catch { /* silent */ }
    setGeocoding(false)
  }, [addressQuery])

  // Compress map canvas to JPEG data URL for upload
  function compressCanvas(sourceCanvas: HTMLCanvasElement): string {
    const maxDim = 1920
    let { width, height } = sourceCanvas
    if (width > maxDim || height > maxDim) {
      const ratio = Math.min(maxDim / width, maxDim / height)
      width = Math.round(width * ratio)
      height = Math.round(height * ratio)
    }
    const offscreen = document.createElement('canvas')
    offscreen.width = width
    offscreen.height = height
    const ctx = offscreen.getContext('2d')
    if (!ctx) return sourceCanvas.toDataURL('image/jpeg', 0.8)
    ctx.drawImage(sourceCanvas, 0, 0, width, height)
    return offscreen.toDataURL('image/jpeg', 0.8)
  }

  // Screenshot / save map as asset with 'map' category
  const [screenshotSaving, setScreenshotSaving] = useState(false)
  const [showFlash, setShowFlash] = useState(false)

  const handleScreenshot = useCallback(async () => {
    if (!mapRef.current || !mapContainerRef.current || screenshotSaving) return
    try {
      setScreenshotSaving(true)
      const map = mapRef.current
      const sourceCanvas = map.getCanvas() as HTMLCanvasElement

      // Composite: draw map canvas, then overlay pin markers
      const composite = document.createElement('canvas')
      composite.width = sourceCanvas.width
      composite.height = sourceCanvas.height
      const ctx = composite.getContext('2d')!
      ctx.drawImage(sourceCanvas, 0, 0)

      // Draw each pin marker at its projected pixel position
      const dpr = window.devicePixelRatio || 1
      const pinRadius = 14 * dpr
      const borderWidth = 2.5 * dpr
      const arrowH = 7 * dpr

      fieldPins.forEach((pin, idx) => {
        const letter = indexToLetter(idx)
        const pt = map.project([pin.longitude, pin.latitude])
        const px = pt.x * dpr
        const py = pt.y * dpr

        // The marker anchor is 'bottom', so the circle center is above the point
        const cx = px
        const cy = py - arrowH - pinRadius

        // Draw white border circle
        ctx.beginPath()
        ctx.arc(cx, cy, pinRadius + borderWidth, 0, Math.PI * 2)
        ctx.fillStyle = '#ffffff'
        ctx.fill()

        // Draw red circle
        ctx.beginPath()
        ctx.arc(cx, cy, pinRadius, 0, Math.PI * 2)
        ctx.fillStyle = '#EF5B6C'
        ctx.fill()

        // Draw arrow triangle below circle
        ctx.beginPath()
        ctx.moveTo(cx - 5 * dpr, cy + pinRadius)
        ctx.lineTo(cx + 5 * dpr, cy + pinRadius)
        ctx.lineTo(cx, cy + pinRadius + arrowH)
        ctx.closePath()
        ctx.fillStyle = '#EF5B6C'
        ctx.fill()

        // Draw letter
        ctx.fillStyle = '#ffffff'
        ctx.font = `bold ${12 * dpr}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(letter, cx, cy)

        // Drop shadow under marker
        ctx.save()
        ctx.shadowColor = 'rgba(0,0,0,0.35)'
        ctx.shadowBlur = 8 * dpr
        ctx.shadowOffsetY = 2 * dpr
        ctx.restore()
      })

      // Draw each user-placed location pin (amber teardrop + label pill).
      // Like the field pins above, these are mapboxgl.Marker DOM overlays and are
      // NOT part of the WebGL canvas, so they must be re-drawn onto the composite
      // by hand — otherwise they are missing from the saved asset.
      const userPinRadius = 11 * dpr
      const userBorder = 2 * dpr
      const userArrowH = 6 * dpr

      markers.forEach(marker => {
        const pt = map.project(marker.coord)
        const px = pt.x * dpr
        const py = pt.y * dpr
        // Marker anchor is 'bottom', so the tip sits on the point; head is above.
        const cx = px
        const cy = py - userArrowH - userPinRadius

        // White border: head circle + pointer triangle
        ctx.save()
        ctx.shadowColor = 'rgba(0,0,0,0.35)'
        ctx.shadowBlur = 4 * dpr
        ctx.shadowOffsetY = 2 * dpr
        ctx.fillStyle = '#ffffff'
        ctx.beginPath()
        ctx.arc(cx, cy, userPinRadius + userBorder, 0, Math.PI * 2)
        ctx.fill()
        ctx.beginPath()
        ctx.moveTo(cx - (5 * dpr + userBorder), cy + userPinRadius)
        ctx.lineTo(cx + (5 * dpr + userBorder), cy + userPinRadius)
        ctx.lineTo(cx, cy + userPinRadius + userArrowH + userBorder)
        ctx.closePath()
        ctx.fill()
        ctx.restore()

        // Colored teardrop: head circle + pointer triangle
        ctx.fillStyle = marker.color || MARKER_COLOR
        ctx.beginPath()
        ctx.arc(cx, cy, userPinRadius, 0, Math.PI * 2)
        ctx.fill()
        ctx.beginPath()
        ctx.moveTo(cx - 5 * dpr, cy + userPinRadius)
        ctx.lineTo(cx + 5 * dpr, cy + userPinRadius)
        ctx.lineTo(cx, cy + userPinRadius + userArrowH)
        ctx.closePath()
        ctx.fill()

        // White center dot
        ctx.fillStyle = '#ffffff'
        ctx.beginPath()
        ctx.arc(cx, cy, 4.5 * dpr, 0, Math.PI * 2)
        ctx.fill()

        // Label pill above the head (mirrors .map-user-pin-label)
        const label = marker.label || ''
        if (label) {
          ctx.font = `600 ${11 * dpr}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          const padX = 7 * dpr
          const padY = 3 * dpr
          const maxTextW = 150 * dpr
          const textW = Math.min(ctx.measureText(label).width, maxTextW)
          const pillW = textW + padX * 2
          const pillH = 11 * dpr + padY * 2
          const pillX = cx - pillW / 2
          const pillY = cy - (userPinRadius + userBorder) - 3 * dpr - pillH
          const r = pillH / 2
          ctx.fillStyle = 'rgba(0,0,0,0.65)'
          ctx.beginPath()
          ctx.moveTo(pillX + r, pillY)
          ctx.arcTo(pillX + pillW, pillY, pillX + pillW, pillY + pillH, r)
          ctx.arcTo(pillX + pillW, pillY + pillH, pillX, pillY + pillH, r)
          ctx.arcTo(pillX, pillY + pillH, pillX, pillY, r)
          ctx.arcTo(pillX, pillY, pillX + pillW, pillY, r)
          ctx.closePath()
          ctx.fill()
          ctx.fillStyle = '#ffffff'
          ctx.fillText(label, cx, pillY + pillH / 2, maxTextW)
        }
      })

      const dataUrl = compressCanvas(composite)

      // Trigger flash
      setShowFlash(true)
      setTimeout(() => setShowFlash(false), 400)

      // After flash, bounce the Assets tab
      setTimeout(() => {
        const assetsTab = Array.from(document.querySelectorAll('.section-tab')).find(el => el.textContent?.trim() === 'Assets')
        if (assetsTab) {
          assetsTab.classList.add('tab-bounce')
          assetsTab.addEventListener('animationend', () => assetsTab.classList.remove('tab-bounce'), { once: true })
        }
      }, 350)

      // Upload in parallel
      const fileName = `map-${estimateId}-${Date.now()}.jpg`
      await assetsService.upload(estimateId, { base64: dataUrl, fileName, category: 'map' })
    } catch (err) {
      console.error('Screenshot save failed:', err)
    } finally {
      setScreenshotSaving(false)
    }
  }, [estimateId, screenshotSaving, fieldPins, markers])

  if (mapError) {
    return (
      <div className="map-panel-error">
        <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" width="32" height="32" style={{ color: 'var(--s400)' }}>
          <path d="M9 20l-5.447-2.724A1 1 0 0 1 3 16.382V5.618a1 1 0 0 1 1.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0 0 21 18.382V7.618a1 1 0 0 0-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
        <p>{mapError}</p>
      </div>
    )
  }

  return (
    <div className="map-panel">
      {/* Toolbar */}
      <div className="map-toolbar">
        <div className="map-address-group">
          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="14" height="14" style={{ flexShrink: 0, color: 'var(--s400)' }}>
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
          </svg>
          <div className="map-address-wrap">
            <input
              className="map-address-input"
              type="text"
              value={addressQuery}
              onChange={(e) => { setAddressQuery(e.target.value); fetchSuggestions(e.target.value) }}
              onKeyDown={(e) => { if (e.key === 'Enter') { setShowSuggestions(false); flyToAddress() } }}
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true) }}
              onBlur={() => { setTimeout(() => setShowSuggestions(false), 200) }}
              placeholder="Enter an address…"
              autoComplete="off"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className="map-address-suggestions">
                {suggestions.map((s, i) => (
                  <button key={i} className="map-address-suggestion" onMouseDown={() => selectSuggestion(s)}>
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="12" height="12" style={{ flexShrink: 0, color: 'var(--s400)' }}>
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                    </svg>
                    <span>{s.place_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button className="map-address-go" onClick={flyToAddress} disabled={geocoding}>
            {geocoding ? '…' : 'Go'}
          </button>
        </div>
        <div className="map-toolbar-divider" />
        <div className="map-toolbar-group">
          <button className={`map-tool-btn${activeTool === 'select' ? ' active' : ''}`} onClick={() => setActiveTool('select')} title="Select">
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16" height="16"><path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" /></svg>
            <span>Select</span>
          </button>
          <button className={`map-tool-btn${activeTool === 'polygon' ? ' active' : ''}`} onClick={() => { setActiveTool('polygon'); setDrawingCoords([]) }} title="Draw Area">
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16" height="16"><path d="M4 5l6-3 6 3 6-3v14l-6 3-6-3-6 3V5z" /></svg>
            <span>Area</span>
          </button>
          <button className={`map-tool-btn${activeTool === 'subtract' ? ' active map-tool-btn-subtract' : ''}`} onClick={() => { setActiveTool('subtract'); setDrawingCoords([]) }} title="Subtract Area (cut-out) — removes its square footage from the total">
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16" height="16"><rect x="3" y="3" width="18" height="18" rx="2" strokeDasharray="3 2" /><path d="M8 12h8" /></svg>
            <span>Subtract</span>
          </button>
          <button className={`map-tool-btn${activeTool === 'line' ? ' active' : ''}`} onClick={() => { setActiveTool('line'); setDrawingCoords([]) }} title="Draw Line">
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16" height="16"><path d="M4 20L20 4" /><circle cx="4" cy="20" r="2" /><circle cx="20" cy="4" r="2" /></svg>
            <span>Line</span>
          </button>
          <button className={`map-tool-btn${activeTool === 'freehand' ? ' active' : ''}`} onClick={() => { setActiveTool('freehand'); setDrawingCoords([]) }} title="Freehand Draw">
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16" height="16"><path d="M3 17c1.5-2 3-3 5-3s3.5 2 5 2 3.5-2 5-4" /><path d="M3 10c1.5-2 3-3 5-3s3.5 2 5 2 3.5-2 5-4" /></svg>
            <span>Freehand</span>
          </button>
          <button className={`map-tool-btn${activeTool === 'marker' ? ' active map-tool-btn-marker' : ''}`} onClick={() => { setActiveTool('marker'); setDrawingCoords([]) }} title="Drop a pin to mark a location (e.g. a pothole) with a label and note">
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16" height="16"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
            <span>Pin</span>
          </button>
        </div>
        <div className="map-toolbar-divider" />
        {/* Screenshot button */}
        <button className="map-tool-btn" onClick={handleScreenshot} disabled={screenshotSaving} title="Save map to Assets">
          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16" height="16"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
          <span>{screenshotSaving ? 'Saving…' : 'Save Map to Assets'}</span>
        </button>
        {/* Drawing hints */}
        {activeTool === 'freehand' && (
          <div className="map-toolbar-hint">
            Click and drag to draw. Release to finish.
            <button className="map-cancel-btn" onClick={() => setActiveTool('select')}>Cancel</button>
          </div>
        )}
        {(activeTool === 'polygon' || activeTool === 'line' || activeTool === 'subtract') && drawingCoords.length > 0 && (
          <div className="map-toolbar-hint">
            Click to add points. Double-click to finish.
            <button className="map-cancel-btn" onClick={() => { setActiveTool('select'); setDrawingCoords([]) }}>Cancel</button>
          </div>
        )}
        {(activeTool === 'polygon' || activeTool === 'line' || activeTool === 'subtract') && drawingCoords.length === 0 && (
          <div className="map-toolbar-hint">{activeTool === 'subtract' ? 'Draw an area to subtract from the total.' : 'Click on the map to start drawing.'}</div>
        )}
        {activeTool === 'marker' && (
          <div className="map-toolbar-hint">
            Click the map to drop a pin. Add a label &amp; note below.
            <button className="map-cancel-btn" onClick={() => setActiveTool('select')}>Done</button>
          </div>
        )}
      </div>

      {/* Map body: the map plus the details column beside it. The details
          column (.map-side) is rendered after the map here but placed to the
          LEFT of it via CSS `order`, so this JSX keeps the map first. */}
      <div className="map-body">

      {/* Map container */}
      <div className="map-container" ref={mapContainerRef}>
        {!mapLoaded && (
          <div className="map-loading">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ animation: 'spin 1s linear infinite' }}>
              <path d="M12 2v4m0 12v4m-7.07-3.93l2.83-2.83m8.48-8.48l2.83-2.83M2 12h4m12 0h4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83" />
            </svg>
            <span>Loading map…</span>
          </div>
        )}
        {showFlash && <div className="map-flash" />}
        {/* Field pins count badge — overlays the map's top-left */}
        {fieldPins.length > 0 && (
          <div className="map-field-pins-badge">
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="14" height="14">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
            </svg>
            {fieldPins.length} field photo{fieldPins.length !== 1 ? 's' : ''} pinned
          </div>
        )}
      </div>

      {/* Details column — rendered to the LEFT of the map (see .map-side order) */}
      <div className="map-side">
        {shapes.length === 0 && markers.length === 0 && (
          <div className="map-side-empty">
            <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <path d="M9 20l-5.447-2.724A1 1 0 0 1 3 16.382V5.618a1 1 0 0 1 1.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0 0 21 18.382V7.618a1 1 0 0 0-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            <span>Draw an area or drop a pin to see its details here.</span>
          </div>
        )}

      {/* Measurements Panel */}
      {shapes.length > 0 && (
        <div className="map-measurements">
          <div className="map-measurements-header">
            <span>Measurements</span>
            <div className="map-unit-toggles">
              {shapes.some(s => s.type === 'polygon' || s.type === 'subtract') && (
                <select className="map-unit-select" value={areaUnit} onChange={(e) => setAreaUnit(e.target.value as AreaUnit)}>
                  <option value="sf">SF</option>
                  <option value="sy">SY</option>
                  <option value="acres">Acres</option>
                </select>
              )}
              {shapes.some(s => s.type === 'line') && (
                <select className="map-unit-select" value={lineUnit} onChange={(e) => setLineUnit(e.target.value as LineUnit)}>
                  <option value="lf">LF</option>
                  <option value="ft">Feet</option>
                </select>
              )}
            </div>
          </div>
          {shapes.map(s => (
            <div
              key={s.id}
              className={`map-measurement-row${selectedShapeId === s.id ? ' selected' : ''}`}
              onClick={() => {
                setSelectedShapeId(s.id)
                if (mapRef.current) {
                  mapRef.current.flyTo({ center: getCentroid(s.coords), duration: 500 })
                }
              }}
            >
              {s.type === 'subtract' ? (
                <span
                  className="map-measurement-color map-measurement-color-subtract"
                  title="Subtracted area (cut-out)"
                />
              ) : (
                <div
                  className="map-measurement-color-wrap"
                  ref={colorPickerFor === s.id ? colorPopoverRef : undefined}
                >
                  <button
                    type="button"
                    className="map-measurement-color"
                    style={{ backgroundColor: s.color }}
                    title="Change color"
                    onClick={(e) => { e.stopPropagation(); setColorPickerFor(colorPickerFor === s.id ? null : s.id) }}
                  />
                  {colorPickerFor === s.id && (
                    <div className="map-color-popover" onClick={(e) => e.stopPropagation()}>
                      {SHAPE_COLORS.map(c => (
                        <button
                          key={c}
                          type="button"
                          className={`map-color-swatch${s.color === c ? ' selected' : ''}`}
                          style={{ backgroundColor: c }}
                          title={c}
                          onClick={(e) => { e.stopPropagation(); updateShapeColor(s.id, c) }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="map-measurement-info">
                {editingLabel === s.id ? (
                  <input
                    className="map-measurement-label-input"
                    defaultValue={s.label}
                    autoFocus
                    onBlur={(e) => updateShapeLabel(s.id, e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') updateShapeLabel(s.id, (e.target as HTMLInputElement).value) }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <button
                    type="button"
                    className="map-measurement-label-btn"
                    title="Rename this measurement"
                    onClick={(e) => { e.stopPropagation(); setEditingLabel(s.id) }}
                  >
                    <span className="map-measurement-label">{s.label}</span>
                    <svg className="map-measurement-label-edit-icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                      <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                    </svg>
                  </button>
                )}
                <span className={`map-measurement-value${s.type === 'subtract' ? ' subtract' : ''}`}>{getDisplayMeasurement(s)}</span>
              </div>
              <button className="map-measurement-delete" onClick={(e) => { e.stopPropagation(); deleteShape(s.id) }} title="Delete">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="14" height="14">
                  <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" />
                </svg>
              </button>
            </div>
          ))}
          <div className="map-measurements-totals">
            {subtractedSF(shapes) > 0 && (
              <div className="map-measurements-subtracted">Subtracted: <strong>−{formatValue(convertArea(subtractedSF(shapes), areaUnit), AREA_UNIT_LABELS[areaUnit])}</strong></div>
            )}
            {shapes.some(s => s.type === 'polygon' || s.type === 'subtract') && (
              <div>Total Area: <strong>{formatValue(convertArea(netPolygonSF(shapes), areaUnit), AREA_UNIT_LABELS[areaUnit])}</strong></div>
            )}
            {/* The net is clamped at 0, so an over-drawn cut-out would otherwise
                read as a legitimate zero-area site. Say so instead. */}
            {subtractedSF(shapes) >= grossPolygonSF(shapes) && subtractedSF(shapes) > 0 && (
              <div className="map-measurements-warning">
                Cut-outs remove at least as much as the areas you drew, so the total is 0. Check for overlapping or stray cut-outs — subtraction is arithmetic, not geometric.
              </div>
            )}
            {shapes.some(s => s.type === 'line') && (
              <div>Total Length: <strong>{formatValue(convertLine(totalLineLF(shapes), lineUnit), LINE_UNIT_LABELS[lineUnit])}</strong></div>
            )}
          </div>
        </div>
      )}

      {/* Markers / Notes Panel */}
      {markers.length > 0 && (
        <div className="map-measurements map-markers-panel">
          <div className="map-measurements-header">
            <span>Markers</span>
          </div>
          {markers.map(m => (
            <div
              key={m.id}
              className={`map-measurement-row map-marker-row${selectedShapeId === m.id ? ' selected' : ''}`}
              onClick={() => {
                setSelectedShapeId(m.id)
                if (mapRef.current) mapRef.current.flyTo({ center: m.coord, duration: 500 })
              }}
            >
              <div
                className="map-marker-dot-wrap"
                ref={colorPickerFor === m.id ? colorPopoverRef : undefined}
              >
                <button
                  type="button"
                  className="map-marker-dot"
                  title="Change marker color"
                  onClick={(e) => { e.stopPropagation(); setColorPickerFor(colorPickerFor === m.id ? null : m.id) }}
                >
                  <svg width="13" height="16" viewBox="0 0 24 30" fill="none">
                    <path d="M12 0C5.37 0 0 5.37 0 12c0 8.25 12 18 12 18s12-9.75 12-18C24 5.37 18.63 0 12 0z" fill={m.color} stroke="#fff" strokeWidth="1.5" />
                    <circle cx="12" cy="12" r="4.5" fill="#fff" />
                  </svg>
                </button>
                {colorPickerFor === m.id && (
                  <div className="map-color-popover" onClick={(e) => e.stopPropagation()}>
                    {SHAPE_COLORS.map(c => (
                      <button
                        key={c}
                        type="button"
                        className={`map-color-swatch${m.color === c ? ' selected' : ''}`}
                        style={{ backgroundColor: c }}
                        title={c}
                        onClick={(e) => { e.stopPropagation(); updateMarkerColor(m.id, c) }}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className="map-marker-info">
                {editingLabel === m.id ? (
                  <input
                    className="map-measurement-label-input"
                    defaultValue={m.label}
                    autoFocus
                    onBlur={(e) => updateMarkerLabel(m.id, e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') updateMarkerLabel(m.id, (e.target as HTMLInputElement).value) }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <button
                    type="button"
                    className="map-measurement-label-btn"
                    title="Rename this marker"
                    onClick={(e) => { e.stopPropagation(); setEditingLabel(m.id) }}
                  >
                    <span className="map-measurement-label">{m.label}</span>
                    <svg className="map-measurement-label-edit-icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                      <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                    </svg>
                  </button>
                )}
                {/* Controlled, not defaultValue+onBlur: an uncommitted note was
                    lost outright if the estimator typed one and then refreshed. */}
                <textarea
                  className="map-marker-note"
                  placeholder="Add a note (e.g. deep pothole, needs patch)…"
                  value={m.note}
                  rows={2}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => updateMarkerNote(m.id, e.target.value)}
                />
              </div>
              <button className="map-measurement-delete" onClick={(e) => { e.stopPropagation(); deleteMarker(m.id) }} title="Delete marker">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="14" height="14">
                  <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      </div>{/* /map-side */}
      </div>{/* /map-body */}
    </div>
  )
}
